import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitLead } from "@/lib/api";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const PROJECT_TYPES = ["Residential", "Commercial", "Industrial", "Industrial Shed", "Mega Project / Township", "Interior", "Renovation", "Engineering / Survey", "Building Materials", "Other"];
const REQUIREMENTS = ["New Construction", "Renovation / Redevelopment", "Design & Planning", "Engineering Consultation", "Interior Design", "Turnkey Project", "Site Survey", "Material Supply", "Other"];

const TABS = [
    { key: "estimate", label: "Get Free Estimate", event: EVENTS.estimateSubmit },
    { key: "consultation", label: "Book Consultation", event: EVENTS.consultationSubmit },
    { key: "site_visit", label: "Request Site Visit", event: EVENTS.siteVisitSubmit },
    { key: "contact", label: "Contact OMSIVO", event: EVENTS.contactSubmit },
];

const FIELDS = {
    estimate: ["name", "mobile", "email", "project_type", "location", "area", "requirement", "message"],
    consultation: ["name", "mobile", "email", "project_type", "location", "requirement", "preferred_date", "preferred_contact"],
    site_visit: ["name", "mobile", "email", "location", "project_type", "area", "preferred_date", "requirement"],
    contact: ["name", "mobile", "email", "subject", "project_type", "location", "message"],
};

const META = {
    name: { label: "Name", type: "text", required: true, placeholder: "Your full name" },
    mobile: { label: "Mobile", type: "tel", required: true, placeholder: "+91" },
    email: { label: "Email", type: "email", placeholder: "you@email.com" },
    project_type: { label: "Project Type", type: "select", options: PROJECT_TYPES },
    location: { label: "Location / Site Location", type: "text", placeholder: "City / Area" },
    area: { label: "Construction / Plot Area (Sq.Ft.)", type: "text", placeholder: "e.g. 1200" },
    requirement: { label: "Requirement", type: "select", options: REQUIREMENTS },
    preferred_date: { label: "Preferred Date", type: "date" },
    preferred_contact: { label: "Preferred Contact Method", type: "select", options: ["Call", "WhatsApp", "Email"] },
    subject: { label: "Subject", type: "text", placeholder: "How can we help?" },
    message: { label: "Message", type: "textarea", placeholder: "Tell us about your project..." },
};

const fieldCls = "h-12 rounded-xl border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-flame";

const Field = ({ fid, id, value, onChange }) => {
    const m = META[fid];
    if (m.type === "textarea") {
        return <Textarea id={id} value={value} onChange={onChange} placeholder={m.placeholder} rows={4}
            data-testid={`form-field-${id}`} className="rounded-xl border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-flame" />;
    }
    if (m.type === "select") {
        return (
            <select id={id} value={value} onChange={onChange} data-testid={`form-field-${id}`}
                className={cn(fieldCls, "w-full appearance-none rounded-xl border px-3 text-sm outline-none focus:border-flame", !value && "text-white/35")}>
                <option value="" className="bg-omsivo-deep">{`Select ${m.label}`}</option>
                {m.options.map((o) => <option key={o} value={o} className="bg-omsivo-deep">{o}</option>)}
            </select>
        );
    }
    return <Input id={id} type={m.type} value={value} onChange={onChange} placeholder={m.placeholder}
        data-testid={`form-field-${id}`} className={fieldCls} />;
};

export const LeadForms = ({ initialTab = "estimate" }) => {
    const [tab, setTab] = useState(initialTab);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    const set = (id) => (e) => setForm((f) => ({ ...f, [id]: e.target.value }));

    const switchTab = (key) => { setTab(key); setForm({}); setDone(null); };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || form.name.trim().length < 2) {
            toast.error("Please enter your name.");
            return;
        }
        if (!form.mobile || form.mobile.replace(/\D/g, "").length < 10) {
            toast.error("Please enter a valid mobile number.");
            return;
        }
        setBusy(true);
        try {
            const res = await submitLead({ ...form, form_type: tab, source_page: window.location.pathname });
            setDone(res);
            trackEvent(TABS.find((t) => t.key === tab).event, { form_type: tab, lead_id: res.lead_id });
            toast.success("Enquiry received", { description: `Lead ID: ${res.lead_id}` });
        } catch {
            toast.error("Something went wrong. Please try WhatsApp or call 73-52-52-52-37.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="flex flex-col items-center px-6 py-16 text-center" data-testid="lead-success">
                <CheckCircle2 className="h-14 w-14 text-flame" />
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">Enquiry received.</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/65">{done.message}</p>
                <p className="mt-5 rounded-full border border-flame/40 bg-flame/10 px-5 py-2 font-mono text-xs tracking-[0.18em] text-flame" data-testid="lead-id-display">
                    LEAD ID · {done.lead_id}
                </p>
                <button onClick={() => { setDone(null); setForm({}); }} data-testid="lead-new-btn"
                    className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 underline-offset-4 hover:text-white hover:underline">
                    Submit another enquiry
                </button>
            </div>
        );
    }

    return (
        <div data-testid="lead-forms">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist" aria-label="Enquiry type">
                {TABS.map((t) => (
                    <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => switchTab(t.key)}
                        data-testid={`lead-tab-${t.key}`}
                        className={cn(
                            "rounded-xl border px-3 py-3 font-display text-[11px] font-bold uppercase tracking-[0.08em] transition-all",
                            tab === t.key ? "border-flame bg-flame text-white" : "border-white/12 bg-white/5 text-white/60 hover:border-flame/50 hover:text-white"
                        )}>
                        {t.label}
                    </button>
                ))}
            </div>

            <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid={`lead-form-${tab}`}>
                {FIELDS[tab].map((id) => (
                    <div key={id} className={cn(META[id].type === "textarea" && "sm:col-span-2")}>
                        <label htmlFor={`${tab}-${id}`} className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                            {META[id].label}{META[id].required && <span className="text-flame"> *</span>}
                        </label>
                        <Field fid={id} id={`${tab}-${id}`} value={form[id] || ""} onChange={set(id)} />
                    </div>
                ))}
                <div className="sm:col-span-2">
                    <button type="submit" disabled={busy} data-testid="lead-submit-btn"
                        className="flex w-full items-center justify-center gap-3 rounded-full bg-flame py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-all hover:-translate-y-0.5 hover:bg-flame-dark disabled:opacity-60">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                        {busy ? "Submitting..." : TABS.find((t) => t.key === tab).label}
                    </button>
                    <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                        This is an enquiry form — not a cost calculator. Detailed estimation runs on the OMSIVO Customer App.
                    </p>
                </div>
            </form>
        </div>
    );
};
